import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

interface Tag {
  id: string;
  name: string;
  slug: string;
  created_at: string;
}

export const TagManager = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("tags")
      .select("*")
      .order("name");

    if (error) {
      toast({ title: "Error loading tags", description: error.message, variant: "destructive" });
    } else {
      setTags(data || []);
    }
    setIsLoading(false);
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const openDialog = (tag?: Tag) => {
    if (tag) {
      setEditingTag(tag);
      setName(tag.name);
      setSlug(tag.slug);
    } else {
      setEditingTag(null);
      setName("");
      setSlug("");
    }
    setIsDialogOpen(true);
  };

  const saveTag = async () => {
    if (!name || !slug) {
      toast({ title: "Name and slug are required", variant: "destructive" });
      return;
    }

    try {
      if (editingTag) {
        const { error } = await supabase
          .from("tags")
          .update({ name, slug })
          .eq("id", editingTag.id);
        if (error) throw error;
        toast({ title: "Tag updated!" });
      } else {
        const { error } = await supabase
          .from("tags")
          .insert({ name, slug });
        if (error) throw error;
        toast({ title: "Tag created!" });
      }

      setIsDialogOpen(false);
      fetchTags();
    } catch (error: any) {
      toast({ title: "Error saving tag", description: error.message, variant: "destructive" });
    }
  };

  const deleteTag = async (id: string) => {
    if (!confirm("Delete this tag? It will be removed from all posts and galleries.")) return;

    try {
      // Delete from junction tables first
      await supabase.from("blog_post_tags").delete().eq("tag_id", id);
      await supabase.from("gallery_tags").delete().eq("tag_id", id);
      
      const { error } = await supabase.from("tags").delete().eq("id", id);
      if (error) throw error;

      toast({ title: "Tag deleted" });
      fetchTags();
    } catch (error: any) {
      toast({ title: "Error deleting tag", description: error.message, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-foreground">Tags</h3>
        <Button onClick={() => openDialog()} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          New Tag
        </Button>
      </div>

      <div className="rounded-xl bg-card border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tags.map((tag) => (
              <TableRow key={tag.id}>
                <TableCell className="font-medium">{tag.name}</TableCell>
                <TableCell className="text-muted-foreground">{tag.slug}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(tag)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteTag(tag.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {tags.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  No tags yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTag ? "Edit Tag" : "New Tag"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!editingTag) setSlug(generateSlug(e.target.value));
                }}
                placeholder="Tag name"
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="tag-slug"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveTag}>
                {editingTag ? "Update Tag" : "Create Tag"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
